import { useEffect, useSyncExternalStore } from 'react';

/**
 * Aufmacher im Hochformat: `public/welcome/hero-hoch.webp` fürs Handy (unter
 * 768 px) statt des breiten Fotos. Der Schalter bleibt, falls die Datei einmal
 * fehlt: `<picture>` hat keinen Rückfall, der Browser zeigte ein kaputtes Bild.
 */
export const HERO_HOCH = true;

/**
 * Darf das Video hinter dem Aufmacher laufen (`public/welcome/hero.mp4`)? Nur
 * auf breiten Bildschirmen (ab 768 px) und nur, wenn niemand weniger Bewegung
 * wünscht. Am Handy wird das `<video>` gar nicht erst gerendert — so lädt der
 * Browser dort auch keine Kopfdaten (`preload="none"` ist der zweite Riegel).
 * Reagiert auf Größenwechsel und auf die Systemeinstellung.
 */
const VIDEO_FRAGEN = ['(min-width: 768px)', '(prefers-reduced-motion: no-preference)'];
function videoErlaubt(): boolean {
  return typeof window !== 'undefined' && VIDEO_FRAGEN.every((q) => window.matchMedia(q).matches);
}
export function useHeroVideo(): boolean {
  return useSyncExternalStore(
    (fn) => {
      if (typeof window === 'undefined') return () => {};
      const listen = VIDEO_FRAGEN.map((q) => window.matchMedia(q));
      for (const l of listen) l.addEventListener('change', fn);
      return () => {
        for (const l of listen) l.removeEventListener('change', fn);
      };
    },
    videoErlaubt,
    () => false,
  );
}

/**
 * Willkommensseite (`#/willkommen`, docs/notes/willkommen.md): Wer die App zum
 * ersten Mal öffnet — kein Turnier auf dem Gerät, nicht angemeldet —, sieht
 * unter `#/` statt der leeren Turnierliste eine Vorstellung der App.
 *
 * Merker `airmail.welcome = seen`: gesetzt, sobald jemand „Zur App" oder
 * „Einführung" drückt. Danach zeigt `#/` immer die Liste — auch wenn noch kein
 * Turnier da ist. Wer die Seite wieder sehen will, nimmt den Link
 * „Willkommensseite" unten bei Impressum · Datenschutz · AGB.
 */
const KEY = 'airmail.welcome';

export function welcomeSeen(): boolean {
  try {
    return localStorage.getItem(KEY) === 'seen';
  } catch {
    // Ohne Speicher (privater Modus mit Sperre) lieber gleich die App zeigen als bei jedem Aufruf die Vorstellung
    return true;
  }
}

export function markWelcomeSeen(): void {
  try {
    localStorage.setItem(KEY, 'seen');
  } catch {
    /* egal */
  }
}

/*
 * Ist die Willkommensseite gerade zu sehen? Die Tour bietet sich auf `#/`
 * von selbst an („Neu hier?"); auf der Willkommensseite steht dafür schon der
 * Knopf „Einführung" — die Einladung darunter wäre doppelt.
 */
let offen = 0;
const listeners = new Set<() => void>();
function melde(): void {
  for (const fn of listeners) fn();
}
export function useWillkommenOffen(): boolean {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => offen > 0,
  );
}
/** Von der Willkommensseite selbst aufgerufen — meldet sich an und beim Verlassen wieder ab */
export function useMeldeWillkommen(): void {
  useEffect(() => {
    offen++;
    melde();
    return () => {
      offen--;
      melde();
    };
  }, []);
}
